import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState, Spinner } from "@/components/common";
import { toast } from "sonner";
import { FileText, Sparkles, Copy } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = ["t-shirt", "mug", "poster", "wall-art", "hoodie"];

export default function Listings() {
  const [topic, setTopic] = useState("");
  const [type, setType] = useState("t-shirt");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/listings").then(({ data }) => setListings(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a product topic first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/listings/generate", { topic, product_type: type });
      setListings((p) => [data, ...p]);
      toast.success("SEO listing generated.");
      setTopic("");
    } catch (e) {
      toast.error(apiError(e, "Listing generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const copy = (l) => {
    navigator.clipboard.writeText(`${l.title}\n\n${l.description}\n\nTags: ${(l.tags || []).join(", ")}`);
    toast.success("Listing copied to clipboard.");
  };

  return (
    <>
      <PageHeader title="Listing Writer" subtitle="Generate keyword-rich titles, descriptions, tags and SEO keywords. Auto-draft — attach to a design in the Design Studio to publish." testid="listings-header">
        <Pill tone="emerald">Auto-Draft</Pill>
      </PageHeader>

      <Card className="p-5" data-testid="listing-input-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <input data-testid="listing-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !loading && generate()} placeholder="Product topic, e.g. 'funny cat coffee lover'" className="lg:col-span-8 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="listing-type-select" className="lg:col-span-2 bg-[#12161F] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
              {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize focus:bg-slate-800">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="generate-listing-button" onClick={generate} disabled={loading} className="lg:col-span-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center">
            {loading ? <Spinner label="Writing…" /> : <><Sparkles className="w-4 h-4" /> Write</>}
          </button>
        </div>
      </Card>

      {listings.length === 0 && !loading ? (
        <Card className="p-6"><EmptyState icon={FileText} title="No listings yet" hint="Generate your first SEO-optimized product listing." /></Card>
      ) : (
        <div className="space-y-4">
          {listings.map((l) => (
            <Card key={l.id} className="p-5" data-testid={`listing-card-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1"><Pill tone="slate">{l.product_type}</Pill></div>
                  <h3 className="font-head text-base font-semibold text-slate-100">{l.title}</h3>
                </div>
                <button data-testid={`copy-listing-${l.id}`} onClick={() => copy(l)} className="p-2 rounded-lg bg-slate-800/60 text-slate-400 hover:text-emerald-300 border border-slate-700/60 transition-colors"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-slate-400 mt-3 whitespace-pre-line leading-relaxed">{l.description}</p>
              {l.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {l.tags.map((t, i) => <Pill key={i} tone="blue">{t}</Pill>)}
                </div>
              )}
              {l.seo_keywords?.length > 0 && (
                <div className="mt-3 text-xs text-slate-500"><span className="font-mono uppercase tracking-wider">SEO:</span> {l.seo_keywords.join(" · ")}</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
